import { useCallback, useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

/**
 * Loads the dropdown choices for an integration's settings form.
 *
 * Settings schema fields can carry `options_source`, naming a connector method whose choices are
 * only knowable from the live vendor instance: Jira projects and Jira transitions are both
 * per-site, so they cannot be enumerated in a static schema.
 *
 * Errors are returned rather than thrown so the form still renders. A failed lookup means the
 * admin cannot pick from a list, which is better than a blank panel.
 */
export default function useTicketingOptions(integrationId, { enabled = true } = {}) {
  const [projects, setProjects] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!integrationId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const [projectsRes, transitionsRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/v1/admin/integrations/${integrationId}/ticketing_projects`),
        fetchWithAuth(`${API_URL}/v1/admin/integrations/${integrationId}/ticketing_transitions`),
      ]);

      if (projectsRes.ok) {
        setProjects((await projectsRes.json()).projects || []);
      } else {
        const body = await projectsRes.json().catch(() => ({}));
        setError(body.error || 'Could not load projects from the ticketing system');
      }

      // Transitions are state-dependent in Jira, so this can legitimately come back empty when
      // there is no issue to inspect yet. That is not an error worth showing.
      if (transitionsRes.ok) {
        setTransitions((await transitionsRes.json()).transitions || []);
      }
    } catch {
      setError('Could not reach the ticketing system');
    } finally {
      setLoading(false);
    }
  }, [integrationId, enabled]);

  useEffect(() => { load(); }, [load]);

  return { projects, transitions, loading, error, reload: load };
}
